import React from "react";
import Icon from "../../assets/icons/Icon";
import { useState, useEffect, useRef, useCallback } from "react";
import ShimmerLoader from "./ShimmerLoader";
import useCurrencySymbol from "./CurrencySymbol";
import DetailRow from "./DetailRow";
import { roundUpTo2 } from "../../util/roundUpTo2";
import { formatDate, isMobileView } from "./CommonFunctions";
import IconButton from "./IconButton";
import { toast } from "react-toastify";

const CustomTable = ({
  columns,
  rows,
  handleEdit,
  handleDelete,
  handleView,
  handleSystemAccess,
  handleOnRow,
  noSticky,
  noHeader,
  selectedRowId,
  paadingRight,
  showTextOnRows,
  loadingRows = 5,
  loading,
  isCompress,
  noShadow,
  allowCompressInMobile,
  // New props for mobile pagination
  allowMobilePagination = false,
  pagination = {}
}) => {
  const mobileView = isMobileView();
  const [selectedRow, setSelectedRow] = useState(null);
  const currencySymbol = useCurrencySymbol();

  // Refs for scroll detection
  const tableContainerRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const isLoadingRef = useRef(false);
  const [showTopArrow, setShowTopArrow] = useState(false);
  const [showBottomArrow, setShowBottomArrow] = useState(false);

  const {
    currentPage = 1,
    totalCount = 0,
    rowsPerPage = 25,
    onPageChange = () => { },
    loading: paginationLoading = false
  } = pagination;

  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  // Check scroll position to show/hide arrows
  const checkScrollPosition = useCallback(() => {
    const container = tableContainerRef.current;
    if (!container || !allowMobilePagination || !mobileView) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isAtTop = scrollTop <= 10;
    const isAtBottom = scrollHeight - scrollTop - clientHeight <= 10;

    setShowTopArrow(isAtTop && currentPage > 1);
    setShowBottomArrow(isAtBottom && currentPage < totalPages);
  }, [currentPage, totalPages, allowMobilePagination, mobileView]);

  // Format date function
  const formatDateFromTimestamp = (value) => {
    if (!value) return "";
    const timestamp = value.toString().length === 10 ? value * 1000 : value;
    return new Date(timestamp).toISOString().split("T")[0];
  };

  // Handle arrow click for pagination
  const handleArrowClick = useCallback((direction) => {
    if (paginationLoading || isLoadingRef.current) return;

    if (direction === 'top' && currentPage > 1) {
      isLoadingRef.current = true;
      console.log('Loading previous page via arrow:', currentPage - 1);

      // Save current scroll position before loading
      const container = tableContainerRef.current;
      if (container) {
        scrollPositionRef.current = container.scrollTop;
      }

      onPageChange(currentPage - 1);

      setTimeout(() => {
        isLoadingRef.current = false;
      }, 1500);
    } else if (direction === 'bottom' && currentPage < totalPages) {
      isLoadingRef.current = true;
      console.log('Loading next page via arrow:', currentPage + 1);

      // Save current scroll position before loading
      const container = tableContainerRef.current;
      if (container) {
        scrollPositionRef.current = container.scrollTop;
      }

      onPageChange(currentPage + 1);

      setTimeout(() => {
        isLoadingRef.current = false;
      }, 1500);
    }
  }, [currentPage, totalPages, paginationLoading, onPageChange]);

  // Set up scroll event listener
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container || !allowMobilePagination || !mobileView) return;

    container.addEventListener('scroll', checkScrollPosition);

    // Initial check
    checkScrollPosition();

    return () => {
      container.removeEventListener('scroll', checkScrollPosition);
    };
  }, [checkScrollPosition, allowMobilePagination, mobileView]);

  // Check scroll position when data changes
  useEffect(() => {
    if (allowMobilePagination && mobileView) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        checkScrollPosition();
      }, 100);
    }
  }, [rows, currentPage, totalPages, allowMobilePagination, mobileView, checkScrollPosition]);

  // Restore scroll position after data loads (only for arrow clicks)
  useEffect(() => {
    if (!allowMobilePagination || !mobileView || paginationLoading) return;

    const container = tableContainerRef.current;
    if (container && scrollPositionRef.current > 0) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        container.scrollTop = scrollPositionRef.current;
        scrollPositionRef.current = 0; // Reset after restoring
        // Re-check scroll position after restoring
        setTimeout(() => checkScrollPosition(), 50);
      }, 0);
    }
  }, [rows, paginationLoading, allowMobilePagination, mobileView, checkScrollPosition]);

  // Reset loading flag when loading state changes
  useEffect(() => {
    if (!paginationLoading) {
      isLoadingRef.current = false;
    }
  }, [paginationLoading]);

  const getNestedValue = (obj, path) => {
    if (!obj || !path) return '';

    const pathParts = path.split('.');
    let value = obj;

    for (const part of pathParts) {
      if (!value) break;

      const arrayMatch = part.match(/(\w+)\[(\d+)\]/);
      if (arrayMatch) {
        const arrayName = arrayMatch[1];
        const arrayIndex = arrayMatch[2];
        value = value[arrayName]?.[arrayIndex];
      } else {
        value = value[part];
      }
    }

    return value || '';
  };

  const calculateColumnTotals = () => {
    const totals = {};
    columns.forEach(column => {
      if (column.showTotal) {
        let sum = 0;
        rows?.forEach(row => {
          let value;
          if (column.renderCell) {
            value = column.renderCell({ row, index: rows.indexOf(row) });
          } else {
            value = getNestedValue(row, column.field);
          }
          value = parseFloat(value) || 0;
          if (!isNaN(value)) {
            sum += value;
          }
        });
        totals[column.field] = sum;
      }
    });
    return totals;
  };

  const columnTotals = calculateColumnTotals();

  // Render mobile pagination arrows with scroll-based visibility
  const renderMobilePaginationArrows = () => {
    if (!allowMobilePagination || !mobileView || totalPages <= 1) return null;

    // Don't show arrows if no more pages to load
    if (!showTopArrow && !showBottomArrow) return null;

    return (
      <>
        {/* Top Arrow - Only show when scrolled to top and not on first page */}
        {showTopArrow && !paginationLoading && (
           <div  onClick={() => handleArrowClick('top')} className="fixed top-4 left-1/2 transform -translate-x-1/2 z-30 bg-indigo-600 text-white px-4 py-2 rounded-full shadow-lg">
            <div className="flex items-center justify-center gap-2 text-sm">
              <div className="w-4 h-4 "> <Icon type="uparrow" /></div>
              Previous
            </div>
          </div>
          // <div className="fixed left-1/2 -translate-x-1/2 top-0 z-30">
          //   <IconButton
          //     size="large"
          //     onClick={() => handleArrowClick('top')}
          //   >
          //     <Icon type="uparrow" />
          //   </IconButton>
          // </div>
        )}

        {/* Bottom Arrow - Only show when scrolled to bottom and not on last page */}
        {showBottomArrow && !paginationLoading && (
            <div  onClick={() => handleArrowClick('bottom')} className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-30 bg-indigo-600 text-white px-4 py-2 rounded-full shadow-lg">
            <div className="flex items-center justify-center gap-2 text-sm">
              <div className="w-4 h-4 "> <Icon type="downarrow" /></div>
              Next
            </div>
          </div>
          // <div className="fixed left-1/2 -translate-x-1/2 bottom-0 z-30">
          //   <IconButton
          //     size="large"
          //     onClick={() => handleArrowClick('bottom')}
          //   >
          //     <Icon type="downarrow" />
          //   </IconButton>
          // </div>
        )}

        {/* Loading Indicator */}
        {paginationLoading && (
          <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-30 bg-indigo-600 text-white px-4 py-2 rounded-full shadow-lg">
            <div className="flex items-center justify-center gap-2 text-sm">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Loading...
            </div>
          </div>
        )}
      </>
    );
  };

  // Render mobile card view
  const renderMobileView = () => {
    if (loading) {
      return (
        <div className="space-y-4">
          {Array.from({ length: loadingRows }).map((_, index) => (
            <div key={index} className="p-4 border-b-1 border-purple-100 bg-purple-soft-gradient animate-pulse h-24"></div>
          ))}
        </div>
      );
    }

    if (showTextOnRows) {
      return (
        <div className="p-4 text-center text-gray-500">
          {showTextOnRows}
        </div>
      );
    }

    return (
      <div className="space-y-3 pb-4">
        {/* Show loading indicator at top when loading previous page */}
        {paginationLoading && currentPage > 1 && (
          <div className="p-3 text-center bg-blue-50 border-b border-blue-200">
            <div className="flex items-center justify-center gap-2 text-blue-600">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              Loading previous page...
            </div>
          </div>
        )}

        {rows?.map((row, index) => (
          <div
            key={row.key || index}
            className={`py-2 ${rows.length - 1 == index ? "" : "border-b-1 border-purple-100"} ${selectedRowId === (row.id || row.key || index) ? "bg-indigo-50" : "bg-white"}`}
            onClick={(e) => {
              const clickedCell = e.target.closest("td");
              if (clickedCell) {
                const columnIndex = Array.from(clickedCell.parentNode.children).indexOf(clickedCell);
                const column = columns[columnIndex];

                if (column?.dontCallOnRowAction) {
                  return;
                }
              }
              if (handleOnRow) {
                handleOnRow(row);
              }
              if (handleEdit) {
                handleEdit(row);
              }
            }}
          >
            <div className="flex flex-col space-y-2 px-2">
              {columns.some(col => col.type === "image" && !col.hideOnMobile) && (
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-[40px] h-[40px] flex items-center justify-center overflow-hidden">
                    {columns.map((col, colIndex) => {
                      if (col.hideOnMobile || col.type !== "image") return null;
                      const cellValue = col.renderCell
                        ? col.renderCell({ row, index })
                        : getNestedValue(row, col.field) || "";
                      return (
                        <img
                          key={`image-${row.id || index}`}
                          src={cellValue || "/noimage/noimage.jpeg"}
                          alt={cellValue || "Image"}
                          className="max-w-full max-h-full rounded-lg object-cover"
                        />
                      );
                    })}
                  </div>

                  <div className="flex-1 flex flex-col space-y-2">
                    {columns.map((col, colIndex) => {
                      if (col.hideOnMobile || col.type === "image") return null;

                      const cellValue = col.renderCell
                        ? col.renderCell({ row, index })
                        : getNestedValue(row, col.field) || "";

                      // Handle boolean type - convert to "Yes"/"No"
                      let displayValue;
                      if (col.type === "boolean") {
                        displayValue = cellValue ? "Yes" : "No";
                      } else if (col.type === "currency") {
                        displayValue = `${currencySymbol} ${cellValue !== "" ? cellValue : "0"}`;
                      } else if (col.type === "percentage") {
                        displayValue = `${cellValue !== "" ? cellValue : "0"} %`;
                      } else {
                        displayValue = cellValue;
                      }

                      // Skip rendering header name if hideHeaderNameOnMobile is true
                      if (col.hideHeaderNameOnMobile) {
                        return (
                          <div key={`${colIndex}-${row.id || index}`} className="flex justify-between">
                            <span className="text-[#000000]">
                              {displayValue}
                            </span>
                          </div>
                        );
                      }

                      return (
                        <div key={`${colIndex}-${row.id || index}`} className="flex justify-between">
                          <tr className="">
                            <td className={`text-(--tab-gray)`}>
                              {col.headerName}:
                            </td>
                            <td className={`pl-2 text-[#000000]`}>
                              {displayValue}
                            </td>
                          </tr>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!columns.some(col => col.type === "image" && !col.hideOnMobile) && (
                <>
                  {columns.map((col, colIndex) => {
                    if (col.hideOnMobile) return null;

                    const cellValue = col.renderCell
                      ? col.renderCell({ row, index })
                      : getNestedValue(row, col.field) || "";

                    // Handle boolean type - convert to "Yes"/"No"
                    let displayValue;
                    if (col.type === "boolean") {
                      displayValue = cellValue ? "Yes" : "No";
                    } else if (col.type === "currency") {
                      displayValue = `${currencySymbol} ${cellValue !== "" ? cellValue : "0"}`;
                    } else if (col.type === "percentage") {
                      displayValue = `${cellValue !== "" ? cellValue : "0"} %`;
                    } else {
                      displayValue = cellValue;
                    }

                    // Skip rendering header name if hideHeaderNameOnMobile is true
                    if (col.hideHeaderNameOnMobile) {
                      return (
                        <div key={`${colIndex}-${row.id || index}`} className="flex justify-between">
                          <span className="text-[#000000] w-full">
                            {displayValue}
                          </span>
                          <span className="text-sm">
                            {col.type === "image" && (
                              <div className="w-[40px] h-[40px] flex overflow-hidden">
                                <img
                                  src={cellValue || "/noimage/noimage.jpeg"}
                                  alt={cellValue || "Image"}
                                  className="rounded-lg object-cover"
                                />
                              </div>
                            )}
                          </span>
                        </div>
                      );
                    }

                    return (
                      <div key={`${colIndex}-${row.id || index}`} className="flex justify-between">
                        <tr className="">
                          <td className={`text-(--tab-gray)`}>
                            {col.headerName}:
                          </td>
                          <td className={`pl-2 text-[#000000]`}>
                            {displayValue}
                          </td>
                        </tr>
                        <span className="text-sm">
                          {col.type === "image" && (
                            <div className="w-[40px] h-[40px] flex overflow-hidden">
                              <img
                                src={cellValue || "/noimage/noimage.jpeg"}
                                alt={cellValue || "Image"}
                                className="rounded-lg object-cover"
                              />
                            </div>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {(handleEdit || handleDelete || handleView) && (
              <div className="flex justify-end gap-4 mt-3 mr-3 pt-3">
                {handleEdit && !row.IsDeleted && (
                  <span
                    className="w-5.5 text-gray-500 hover:text-green-600 inline-block cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(row);
                    }}
                    label="edit"
                  >
                    <Icon type="edit" />
                  </span>
                )}
                {handleDelete && !row.IsDeleted && (
                  <span
                    className="w-5 inline-block text-gray-500 hover:text-red-600 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(row);
                    }}
                    label="delete"
                  >
                    <Icon type="delete" />
                  </span>
                )}
                {handleView && (
                  <span
                    className="w-5 inline-block text-gray-500 hover:text-purple-900"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleView(row);
                    }}
                    label="View"
                  >
                    <Icon type="view" />
                  </span>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Loading indicator for next page at bottom */}
        {paginationLoading && currentPage < totalPages && (
          <div className="p-4 text-center">
            <div className="flex items-center justify-center gap-2 text-gray-500">
              <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              Loading more products...
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render desktop table view (unchanged)
  const renderDesktopView = () => {
    return (
      <table className="w-full border-separate border-spacing-0">
        {!noHeader && (
          <thead className={`z-10 ${noSticky ? "" : "sticky top-0"}`}>
            <tr className="bg-indigo-soft-gradient text-gray-700">
              {columns.map((col, colIndex) => (
                <th
                  key={colIndex}
                  className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider border-b border-gray-200
              ${col.align === "right" ? "text-right" : "text-left"} ${(col.type == "number" || col.type == "currency") && "text-right"}
              ${col.width ? col.width : ""}
            `}
                >
                  {col.headerName}
                </th>
              ))}
              {(handleEdit || handleDelete || handleView) && (
                <th className="px-1 w-40 py-2 font-medium text-center text-gray-600 min-w-[120px]">Actions</th>
              )}
            </tr>
          </thead>
        )}
        <tbody className={`${loading || showTextOnRows ? "" : "bg-white/60 backdrop-blur-sm"}`}>
          {loading ? (
            <ShimmerLoader
              columns={columns}
              rows={loadingRows}
              hasActions={handleEdit || handleDelete || handleView}
            />
          ) : showTextOnRows ? (
            <tr>
              <td colSpan={columns.length + ((handleEdit || handleDelete || handleView) ? 1 : 0)}
                className="py-4 text-center text-gray-500">
                {showTextOnRows}
              </td>
            </tr>
          ) : (
            <>
              {rows?.map((row, index) => (
                <tr
                  key={row.key || index}
                  className={`hover:bg-indigo-50/40 transition-colors duration-150 group ${handleOnRow || handleEdit ? "cursor-pointer" : ""
                    } ${selectedRowId === (row.id || row.key || index) ? "bg-indigo-50/40" : ""
                    }`}
                  onClick={(e) => {
                     const clickedCell = e.target.closest("td");
    if (clickedCell) {
      const columnIndex = Array.from(clickedCell.parentNode.children).indexOf(clickedCell);
      const column = columns[columnIndex];

      if (column?.dontCallOnRowAction) {
        return;
      }
    }
                    if (handleOnRow) {
                      handleOnRow(row);
                    }
                    else if (handleEdit) {
                      if (row.hasOwnProperty('IsDeleted')) {
                        // If IsDeleted property exists, only allow edit when it's false/undefined
                        if (!row?.IsDeleted) {
                          handleEdit(row);
                        }
                        if (row.IsDeleted) {
                          toast.info("selected item is deleted !");
                        }
                      } else {
                        // If IsDeleted property doesn't exist at all, allow edit
                        handleEdit(row);
                      }
                    }
                  }}
                // onClick={() => handleOnRow?.(row)}
                >
                  {columns?.map((col, colIndex) => {
                    const cellValue = col.renderCell
                      ? col.renderCell({ row, index })
                      : getNestedValue(row, col.field) || "";

                    return (
                      <td
                        key={`${colIndex} ${row.id || index}-${col.field}`}
                        className={`px-3 py-2 align-top ${col.align === "right" ? "text-right" : "text-left"
                          } ${(col.type === "number" || col.type === "currency" || col.type === "percentage") ? "text-right" : ""
                          } ${index !== rows.length - 1 ? "border-b-1 border-purple-100" : ""
                          } ${(col.type === "date" || col.type === "number" || col.type === "currency") ? "whitespace-nowrap" : ""
                          }`}
                      >
                        {col.type === "image" ? (
                          <div className="w-[100px] h-[100px] flex items-center justify-center overflow-hidden">
                            <img
                              src={cellValue || "/noimage/noimage.jpeg"}
                              alt={cellValue || "Image"}
                              className={`${!cellValue ? "object-cover" : "max-w-full max-h-full"} rounded-lg`}
                            />
                          </div>
                        ) : col.type === "currency" ? (
                          `${currencySymbol} ${cellValue !== "" ? roundUpTo2(cellValue) : "0"}`
                        ) : col.type === "date" ? (
                          cellValue ? formatDate(cellValue) : ""
                        ) : col.type === "percentage" ? (
                          `${cellValue !== "" ? cellValue : "0"} %`
                        ) : col.type === "boolean" ? (
                          `${cellValue ? "yes" : "No"}`
                        ) : (
                          cellValue !== "" ? cellValue : (col.type === "number" ? "0" : "-")
                        )}
                      </td>
                    );
                  })}
                  {(handleEdit || handleDelete || handleView) && (
                    <td className={`px-1 w-40 py-2 text-center align-top ${index !== rows.length - 1 ? "border-b-1 border-purple-100" : ""}`}>
                      <div className="flex justify-center gap-6 items-center">
                        {handleEdit && !row.IsDeleted && (
                          <span
                            className="w-5.5 text-gray-500 hover:text-green-600 inline-block cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(row);
                            }}
                            label="edit"
                          >
                            <Icon type="edit" />
                          </span>
                        )}
                        {handleDelete && !row.IsDeleted && (
                          <span
                            className="w-5 inline-block text-gray-500 hover:text-red-600 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(row, index);
                            }}
                            label="delete"
                          >
                            <Icon type="delete" />
                          </span>
                        )}
                        {handleView && (
                          <span
                            className="w-5 inline-block text-gray-500 hover:text-purple-900 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleView(row);
                            }}
                            label="View"
                          >
                            <Icon type="view" />
                          </span>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {(Object.keys(columnTotals).length > 0 && rows.length > 0) && (
                <tr className="bg-gray-50 font-medium">
                  {columns.map((col, colIndex) => {
                    if (col.showTotal) {
                      return (
                        <td
                          key={`total-${col.field}`}
                          className={`px-3 py-2 ${col.align === "right" ? "text-right" : "text-left"
                            } ${(col.type === "number" || col.type === "currency") ? "text-right whitespace-nowrap" : ""} border-t-1 border-purple-100`}

                        >
                          {col.type === "currency" ? (
                            `${currencySymbol} ${columnTotals[col.field].toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}`
                          ) : (
                            columnTotals[col.field].toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })
                          )}
                        </td>
                      );
                    }
                    return (
                      <td
                        key={`total-${col.field}`}
                        className={`px-3 py-2 ${col.align === "right" ? "text-right" : "text-left"
                          } border-t-1 border-purple-100`}
                      >
                        {colIndex === 0 ? 'Total' : ''}
                      </td>
                    );
                  })}
                  {(handleEdit || handleDelete || handleView) && (
                    <td className="px-1 w-40 py-2 text-center"></td>
                  )}
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
    );
  };

  return (
    <div className={`bg-white backdrop-blur-sm ${noShadow ? "border border-solid border-[#eee]" : "border border-solid border-[#eee]"} h-full flex flex-col`}>
      <div className="w-full flex-1 overflow-hidden flex flex-col">
        <div
          ref={tableContainerRef}
          className="relative flex-1 overflow-auto custom-scrollable"
          onScroll={checkScrollPosition}
        >
          {(mobileView && allowCompressInMobile) ? renderMobileView() : renderDesktopView()}
        </div>
        {renderMobilePaginationArrows()}
      </div>
    </div>
  );
};

export default CustomTable;